/**
 * Subscription Management Type Definitions
 * Based on API_DOCUMENTATION_SUBSCRIPTION_MANAGEMENT.md
 */

// ========== Enums ==========

/**
 * Plan duration expressed as a number of months (1–99).
 * The backend stores/returns this as `durationMonths` (integer).
 */
export type PlanDuration = number;

export const MIN_PLAN_DURATION_MONTHS = 1;
export const MAX_PLAN_DURATION_MONTHS = 99;

export type SubscriptionStatus = 'ACTIVE' | 'EXPIRED' | 'CANCELLED';

export type PaymentStatus = 'PENDING' | 'COMPLETED' | 'FAILED';

export type UserTier = 'FREE' | 'PREMIUM' | 'VIP';

// ========== Subscription Plan Features ==========

export interface SubscriptionPlanFeatures {
  downloadEnabled: boolean;
  maxDownloads: number | null; // null = unlimited
  hdEnabled: boolean;
  uhdEnabled: boolean;
  adsEnabled: boolean;
  maxDevices: number | null; // null = unlimited
  offlineViewing: boolean;
}

// ========== Subscription Plan Interfaces ==========

export interface SubscriptionPlan {
  planId: string;
  name: string;
  description: string | null;
  durationMonths: PlanDuration;
  price: number;
  originalPrice: number | null;
  discountPercentage: number | null;
  finalPrice: number;
  currency: string;
  tierGranted: UserTier;
  features: SubscriptionPlanFeatures;
  isFeatured: boolean;
  sortOrder: number;
  isActive?: boolean;
}

export interface SubscriptionPlanCreateRequest {
  name: string;
  description?: string;
  durationMonths: PlanDuration;
  price: number;
  currency?: string; // Default: MMK
  discountPercentage?: number;
  originalPrice?: number;
  tierGranted: UserTier;
  downloadEnabled?: boolean;
  maxDownloads?: number | null;
  hdEnabled?: boolean;
  uhdEnabled?: boolean;
  adsEnabled?: boolean;
  maxDevices?: number | null;
  offlineViewing?: boolean;
  isFeatured?: boolean;
  sortOrder?: number;
}

export interface SubscriptionPlanUpdateRequest {
  name?: string;
  description?: string;
  price?: number;
  discountPercentage?: number;
  originalPrice?: number;
  isFeatured?: boolean;
  sortOrder?: number;
  // Note: Features can also be updated
  downloadEnabled?: boolean;
  maxDownloads?: number | null;
  hdEnabled?: boolean;
  uhdEnabled?: boolean;
  adsEnabled?: boolean;
  maxDevices?: number | null;
  offlineViewing?: boolean;
}

export interface SubscriptionPlanQueryParams {
  isActive?: boolean;
  tierGranted?: UserTier;
  durationMonths?: PlanDuration;
  isFeatured?: boolean;
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface SubscriptionPlansData {
  items: SubscriptionPlan[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// ========== User Subscription Interfaces ==========

export interface Subscription {
  subscriptionId: string;
  plan: SubscriptionPlan;
  planDurationMonths: PlanDuration;
  amountPaid: number;
  currency: string;
  startDate: string; // ISO 8601
  endDate: string; // ISO 8601
  status: SubscriptionStatus;
  paymentStatus: PaymentStatus;
  daysRemaining: number | null; // null if expired
  isActive: boolean;
}

export interface SubscriptionCreateRequest {
  userId: string;
  planId: string;
  startDate?: string; // ISO 8601, optional, defaults to current date/time
  notes?: string; // Admin notes
}

// ========== Helper Arrays for Dropdowns ==========

/**
 * Common preset durations used for quick filters (kept for backward compatibility).
 * Plan duration is now an arbitrary integer between MIN_PLAN_DURATION_MONTHS and
 * MAX_PLAN_DURATION_MONTHS, but these presets are still useful for filter dropdowns.
 */
export const PLAN_DURATIONS: { value: PlanDuration; label: string }[] = [
  { value: 1, label: '1 Month' },
  { value: 3, label: '3 Months' },
  { value: 6, label: '6 Months' },
  { value: 12, label: '1 Year' },
];

export const SUBSCRIPTION_STATUSES: { value: SubscriptionStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export const PAYMENT_STATUSES: { value: PaymentStatus; label: string }[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
];

export const USER_TIERS: { value: UserTier | ''; label: string }[] = [
  { value: '', label: 'All Tiers' },
  { value: 'FREE', label: 'Free' },
  { value: 'PREMIUM', label: 'Premium' },
  { value: 'VIP', label: 'VIP' },
];

// ========== Helper Functions ==========

/**
 * Calculate final price based on price and discount
 */
export const calculateFinalPrice = (price: number, discountPercentage?: number | null): number => {
  if (!discountPercentage || discountPercentage <= 0) {
    return price;
  }
  return price * (1 - discountPercentage / 100);
};

/**
 * Format a numeric duration (in months) into a human-readable display string.
 * Examples: 1 -> "1 Month", 3 -> "3 Months", 12 -> "1 Year", 24 -> "2 Years",
 * 18 -> "18 Months".
 */
export const formatDuration = (months: PlanDuration | null | undefined): string => {
  if (months == null || !Number.isFinite(months) || months <= 0) {
    return '—';
  }
  if (months === 1) return '1 Month';
  if (months % 12 === 0) {
    const years = months / 12;
    return years === 1 ? '1 Year' : `${years} Years`;
  }
  return `${months} Months`;
};

/**
 * Format tier enum to display string
 */
export const formatTier = (tier: UserTier): string => {
  const tierMap: Record<UserTier, string> = {
    FREE: '🆓 Free',
    PREMIUM: '⭐ Premium',
    VIP: '👑 VIP',
  };
  return tierMap[tier];
};

/**
 * Get tier color class
 */
export const getTierColor = (tier: UserTier): string => {
  const colorMap: Record<UserTier, string> = {
    FREE: 'bg-gray-100 text-gray-800',
    PREMIUM: 'bg-yellow-100 text-yellow-800',
    VIP: 'bg-purple-100 text-purple-800',
  };
  return colorMap[tier];
};

/**
 * Get status color class
 */
export const getSubscriptionStatusColor = (status: SubscriptionStatus): string => {
  const colorMap: Record<SubscriptionStatus, string> = {
    ACTIVE: 'bg-green-100 text-green-800',
    EXPIRED: 'bg-red-100 text-red-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
  };
  return colorMap[status];
};

/**
 * Format price with currency
 */
export const formatPrice = (price: number, currency: string = 'MMK'): string => {
  return `${price.toLocaleString()} ${currency}`;
};

